import { ForwardedRef, forwardRef, useCallback, useState } from 'react';
import { MenuItem } from '@mantine/core';
import clsx from 'clsx';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';
import { useScrollTopOffset } from '../layout/LayoutContext';
import { Menu } from '../menu/Menu';
import { Tooltip } from '../tooltip/Tooltip';

import { WidgetAction, WidgetMoreAction, WidgetProps } from './Widget';

import styles from './widget.module.scss';

type ActionState = { loading: boolean };

export type WidgetHeaderProps = {
  actions?: WidgetProps['actions'];
  collapsible?: WidgetProps['collapsible'];
  enableScrollElementToTop?: boolean;
  opened?: boolean;
  toggleContent: () => void;
  title: WidgetProps['title'];
};

export function WidgetHeader({
  actions,
  collapsible = false,
  enableScrollElementToTop = false,
  opened = true,
  toggleContent,
  title,
}: WidgetHeaderProps) {
  const offset = useScrollTopOffset();

  const [actionStates, setActionStates] = useState<ActionState[]>(
    actions?.map((_) => ({ loading: false })) ?? []
  );

  const triggerAction = useCallback(
    (action: WidgetAction | WidgetMoreAction, idx?: number) => {
      const updateActionItemAt = (idx: number, data: Partial<ActionState>) => {
        const updatedActionStates = [...actionStates];
        updatedActionStates.splice(idx, 1, {
          ...actionStates[idx],
          ...data,
        });
        setActionStates(updatedActionStates);
      };

      // use loading indicator for async action callbacks only
      return action.cb?.constructor.name === 'AsyncFunction'
        ? async () => {
            idx != null && updateActionItemAt(idx, { loading: true });
            await action.cb?.();
            idx != null && updateActionItemAt(idx, { loading: false });
          }
        : action.cb;
    },
    [actionStates]
  );

  const hasContents = actions?.length || collapsible || title;

  if (!hasContents) {
    return null;
  }

  return (
    <div className={styles['header']}>
      {collapsible && (
        <button
          aria-label="Collapse widget"
          className={clsx(
            styles['header__collapsible-icon'],
            opened && styles['header__collapsible-icon--opened']
          )}
          type="button"
          onClick={toggleContent}
        />
      )}
      <div
        className={clsx(styles['header__title'], {
          [styles['header__title__clickable']]: enableScrollElementToTop,
        })}
        onClick={(e) => {
          if (enableScrollElementToTop) {
            window.scrollBy({
              top:
                (e.currentTarget.parentElement?.getBoundingClientRect().top ??
                  0) - (offset.current ?? 0),
              behavior: 'smooth',
            });
          }
        }}
      >
        {title}
      </div>
      <div className={styles['header__actions']}>
        {actions?.map((action, idx) =>
          action.moreActions ? (
            <MoreMenu
              action={action}
              idx={idx}
              key={action.icon.name}
              loading={actionStates[idx].loading}
              triggerAction={triggerAction}
            />
          ) : (
            <IconButton
              action={action}
              key={action.icon.name}
              loading={actionStates[idx].loading}
              onClick={triggerAction(action, idx)}
            />
          )
        )}
      </div>
    </div>
  );
}

type IconButtonProps = {
  action: WidgetAction;
  loading?: boolean;
  onClick?: () => void | Promise<void>;
};

const IconButton = forwardRef(
  (
    { action, loading, onClick }: IconButtonProps,
    ref: ForwardedRef<HTMLButtonElement | null>
  ) => {
    const hasLabel = Boolean(action.label);

    return (
      <Button
        ref={ref}
        loading={loading}
        disabled={action.disabled}
        icon={
          action.icon.svgIcon ? (
            <SVGIcon name={action.icon.name} key={action.icon.name} />
          ) : (
            <GoogleFontIcon
              key={action.icon.name}
              name={action.icon.name}
              solid={action.icon.solid ?? false}
              small
            />
          )
        }
        onClick={onClick}
        title={action.title}
        variant={hasLabel ? 'primary' : 'text'}
        className={clsx([!hasLabel && styles['header__action']])}
      >
        {action.label}
      </Button>
    );
  }
);

type MoreMenuProps = {
  action: WidgetAction;
  idx: number;
  loading?: boolean;
  triggerAction: (
    action: WidgetAction | WidgetMoreAction,
    idx?: number
  ) => (() => void | Promise<void>) | undefined;
};

function MoreMenu({ action, idx, loading, triggerAction }: MoreMenuProps) {
  const [opened, setOpened] = useState(false);

  return (
    <Menu
      onChange={setOpened}
      opened={opened}
      offset={0}
      position="bottom-end"
      menuTarget={
        <IconButton
          action={action}
          loading={loading}
          onClick={() => setOpened(true)}
        />
      }
      menuDropdown={action.moreActions?.map((ma) => (
        <MenuItem
          className={styles['menu-item']}
          key={ma.label}
          disabled={ma.disabled}
          onClick={triggerAction(ma, idx)}
        >
          <span>{ma.label}</span>
          {ma.tooltip != null && (
            <Tooltip label={ma.tooltip} usePortal={true} width="200px">
              <GoogleFontIcon name="info" />
            </Tooltip>
          )}
        </MenuItem>
      ))}
    />
  );
}
